"use client";

import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { LOCALE_COOKIE, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { setCookie } from "@/lib/utils/cookie-utils";

const LABELS: Record<Locale, string> = {
  fa: "فارسی",
  en: "English",
  ar: "العربية",
};

interface LocaleSwitcherProps {
  /** Outer-container override (e.g. `w-full` in the mobile sheet). */
  className?: string;
  /** Fired after switching — lets a parent (e.g. the mobile sheet) close. */
  onSelect?: () => void;
}

export function LocaleSwitcher({ className, onSelect }: LocaleSwitcherProps) {
  const active = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();

  const switchTo = (locale: Locale) => {
    // Remember the choice for the switcher's initial state (not used for routing).
    setCookie(LOCALE_COOKIE, locale, { maxAge: 31536000, sameSite: "Lax" });
    router.replace(pathname, { locale });
    onSelect?.();
  };

  return (
    <div
      className={cn("flex gap-1 rounded-lg bg-bg-secondary p-1", className)}
      role="group"
      aria-label="Language"
    >
      {(Object.keys(LABELS) as Locale[]).map((locale) => (
        <button
          key={locale}
          type="button"
          lang={locale}
          aria-current={locale === active ? "true" : undefined}
          onClick={() => switchTo(locale)}
          className={cn(
            "flex-1 rounded-md px-3 py-1.5 text-sm transition-colors",
            locale === active
              ? "bg-bg-primary text-text-primary font-semibold"
              : "text-text-secondary hover:text-text-primary",
          )}
        >
          {LABELS[locale]}
        </button>
      ))}
    </div>
  );
}
